import { Fragment } from 'react';
import { IconUser } from '@tabler/icons-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { EmptyState } from '@/components/common/EmptyState';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { useGeoMarkupStore } from '@/store';
import { isSameDay } from '@/utils/dateHelpers';
import type { GroupPosition, MessagesListProps } from '../types';
import { COMMENT_EMPTY_STATE_ICON_SIZE } from '../constants';
import { CommentMessage } from './CommentMessage';
import styles from './MessagesList.module.css';

export const MessagesList = ({
  comments,
  currentUser,
  readOnly = false,
  editingCommentId,
  refs,
  onScroll,
  onEdit,
  onDelete,
  onCopy,
  formatDate,
}: MessagesListProps) => {
  const { t } = useTranslation();
  const { formatDaySeparator } = useLocalizedDate();
  const userStatuses = useGeoMarkupStore(
    (state) => state.presence.userStatuses
  );
  const hasMessages = comments.length > 0;

  return (
    <AppScrollArea
      fill
      offsetScrollbars="present"
      viewportRef={(el) => {
        if (refs.current) {
          refs.current.messagesContainer = el;
        }
      }}
      onScrollPositionChange={onScroll}
      classNames={{
        viewport: clsx(editingCommentId && styles.viewportLocked),
        content: styles.content,
      }}
    >
      {!hasMessages ? (
        <EmptyState
          icon={<IconUser size={COMMENT_EMPTY_STATE_ICON_SIZE} />}
          title={t('comments.noCommentsYet')}
          size="sm"
          className={styles.emptyState}
        />
      ) : (
        comments.map((comment, index) => {
          const isCurrentUser = !readOnly && comment.authorId === currentUser;
          const prev = index > 0 ? comments[index - 1] : undefined;
          const next =
            index < comments.length - 1 ? comments[index + 1] : undefined;

          const isFirstInGroup =
            !prev ||
            prev.authorId !== comment.authorId ||
            Math.floor(comment.createdAt.getTime() / 60000) !==
              Math.floor(prev.createdAt.getTime() / 60000);

          const isLastInGroup =
            !next ||
            next.authorId !== comment.authorId ||
            Math.floor(comment.createdAt.getTime() / 60000) !==
              Math.floor(next.createdAt.getTime() / 60000);

          let groupPosition: GroupPosition;
          if (isFirstInGroup && isLastInGroup) groupPosition = 'solo';
          else if (isFirstInGroup) groupPosition = 'first';
          else if (isLastInGroup) groupPosition = 'last';
          else groupPosition = 'middle';

          const showDaySeparator =
            !prev || !isSameDay(comment.createdAt, prev.createdAt);

          return (
            <Fragment key={comment.id}>
              {showDaySeparator && (
                <div className={styles.daySeparator}>
                  <div className={styles.daySeparatorLine} />
                  <span className={styles.daySeparatorText}>
                    {formatDaySeparator(comment.createdAt)}
                  </span>
                  <div className={styles.daySeparatorLine} />
                </div>
              )}
              <CommentMessage
                comment={comment}
                isCurrentUser={isCurrentUser}
                authorPresenceStatus={userStatuses[comment.authorId] ?? null}
                groupPosition={groupPosition}
                editingCommentId={editingCommentId}
                onEdit={onEdit}
                onDelete={onDelete}
                onCopy={onCopy}
                formatDate={formatDate}
              />
            </Fragment>
          );
        })
      )}
      <div
        ref={(el) => {
          if (refs.current) {
            refs.current.messagesEnd = el;
          }
        }}
      />
    </AppScrollArea>
  );
};
